import { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { DefaultAndValue } from "../util/restore";
import { EditorHeaderWithPresetChooser } from "./EditorHeaderWithPresetChooser";

interface PresetMultilineTextEditorProps {
  requestFocus?: boolean;
  label?: string;
  value: DefaultAndValue;
  onChange: (newValue: DefaultAndValue) => void;
  onEnter?: () => void;
  rows?: number;
}

export function PresetMultilineTextEditor(props: PresetMultilineTextEditorProps) {
  return GenericMultilineTextEditor(props);
}

interface MultilineTextEditorProps {
  requestFocus?: boolean;
  label?: string;
  value: string;
  onChange: (newValue: string) => void;
  onEnter?: () => void;
  rows?: number;
}

export function MultilineTextEditor(props: MultilineTextEditorProps) {
  return GenericMultilineTextEditor(props);
}

interface GenericMultilineTextEditorProps {
  requestFocus?: boolean;
  label?: string;
  value: string | DefaultAndValue;
  onChange: (newValue: string | DefaultAndValue) => void;
  onEnter?: () => void;
  rows?: number;
}

function GenericMultilineTextEditor(props: GenericMultilineTextEditorProps) {
  const { label, value, onChange, rows = 3 } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isValueWithoutDefault = (typeof value === 'string');

  useLayoutEffect(() => {
    if (textareaRef.current && props.requestFocus) {
      textareaRef.current.focus();
    }
  }, [textareaRef, props.requestFocus]);

  const maybeConvertChange = (v) => {
    if (isValueWithoutDefault) {
      onChange(v)
    } else {
      onChange({
        default: value.default,
        value: v
      })
    }
  } 

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    maybeConvertChange(e.target.value);
  };

  const inputValue = isValueWithoutDefault ? value : value["value"];

  return (
    <Container>
      <EditorHeaderWithPresetChooser 
        label={label}
        value={value}
        onChange={maybeConvertChange}
      />
      <TextArea
        ref={textareaRef}
        $rows={rows}
        value={inputValue}
        onChange={(e) => handleChange(e)}
        onKeyDown={(e) => {
          if (props.onEnter && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevent default to avoid newline
            props.onEnter();
          }
        }}
      />
    </Container>
  );
}

// Styled components
const Container = styled.div`
  flex-grow: 1;
`;

const TextArea = styled.textarea<{ $rows: number }>`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  font-family: Arial, Helvetica, sans-serif;
  border-radius: 0.25rem;
  min-height: ${(props) => props.$rows * 1.5}rem;
  resize: vertical;
`;
